import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { CONFIG_URLS_API } from 'src/environments/config-envs';
import { environment } from 'src/environments/environment';
import { IBaseResponse } from '../central-ajuste-precos/central.interface';
import { LocalStorageService } from '../services/localstorage.service';
import { SnackBarService } from '../services/snack-bar-service.service';

export type PrecoAlertaDirecao = 'subiu' | 'desceu';

/** Janela temporal usada para qualificar a variação. */
export type PrecoAlertaJanela = '24h' | '30d';

export interface PrecoAlertaDestaque {
  /** Chave estável para `track` em listas */
  chave: string;
  direcao: PrecoAlertaDirecao;
  /**
   * Janela usada para descrever a variação ao usuário.
   * `'24h'` indica comparação imediata (`numPrecoAnterior`→`numPrecoNaBombaAtual`);
   * `'30d'` indica tendência mensal vinda de `monitor30d`.
   */
  janela: PrecoAlertaJanela;
  /**
   * Variação percentual absoluta (sempre positiva). Pode ser `null` quando o
   * item só tem sinal qualitativo (`monitor30d`) e não há preço anterior
   * para calcular o delta.
   */
  percentual: number | null;
  tituloLocal: string;
  /** Preço anterior — opcional para alertas de origem `'30d'` sem histórico. */
  precoAnterior?: number;
  precoAtual: number;
  momentoRelativo: string;
  /** ISO 8601 para o atributo `datetime` do `<time>`, quando disponível */
  momentoIso?: string;
  caminhoesAgendados?: number;
  /**
   * Posto mais perto e mais barato sugerido como alternativa. Presente
   * apenas quando os 4 campos de origem (`distancia`,
   * `vlrPrecoMaisPertoMaisBarato`, `codPostoMaisPertoMaisBarato` e
   * `dscFantasiaPostoMaisPerto`) vêm preenchidos no item da API.
   */
  melhorAlternativa?: PrecoAlertaMelhorAlternativa;
}

export interface PrecoAlertaMelhorAlternativa {
  codPosto: number;
  fantasia: string;
  distanciaKm: number;
  preco: number;
}

export interface PrecoBombaResponse extends IBaseResponse {
  retorno: {
    economiaPotencial: number;
  };
}

export interface PrecoBombaFrotaResponse extends IBaseResponse {
  retorno: FrotaRetornoItem[];
}

export interface DashboardData {
  veiculosAfetados: number;
  veiculosAfetadosUltimaHora: number;
  postosRuptura: number;
  alertasPreco: number;
  economiaPotencial: number;
  alertasPrecoDetalhe: PrecoAlertaDestaque[];
}

/** Indicador booleano string vindo do back ('SIM' | 'NAO'). */
export type SimNao = 'SIM' | 'NAO';

/** Tendência do preço nos últimos 30 dias retornada pelo monitor. */
export type Monitor30d = '30D-SUBIU' | '30D-DESCEU' | '';

/** Tendência do preço nas últimas 24 horas retornada pelo monitor. */
export type Monitor24h = '24H-SUBIU' | '24H-DESCEU' | '';

/** Flag numérica 0/1 (bloqueio do posto). */
export type FlagBit = 0 | 1;

/**
 * Item retornado pela API `Frota` (e pelo ficheiro estático
 * `assets/response.json` usado em desenvolvimento).
 *
 * Espelha fielmente o payload, com `null` preservado nos campos em que o
 * back envia `null` explicitamente. Campos opcionais (`?`) são os que podem
 * vir omitidos em respostas mais antigas.
 */
export interface FrotaRetornoItem {
  // Identificação do posto
  dscCnpjPosto: string;
  codPosto: number;
  dscFantasia?: string;
  dscEst?: string;
  dscMun?: string;
  dscEndereco?: string;
  numEndereco?: number;
  dscBairro?: string;

  // Combustível
  zeCodcomb?: number;
  dscComb: string;

  // Preços
  numPrecoNaBombaAtual: number;
  numPrecoAnterior?: number | null;
  descontoPercentual: number;
  descontoReais?: number;
  precoFinal: number;
  precoRfcZymux?: number | null;
  precoPortal?: number | null;
  precoSpsMedio?: number | null;
  precoComAep?: number | null;
  precoUltimaUtilizacao?: number;
  vlrPrecoDesconto?: number | null;

  // Datas (ISO 8601)
  datInicio?: string | null;
  datVigencia?: string | null;
  datUltimoAbastecimento: string | null;
  datTreinamentoQrcode?: string | null;

  // Status / sinalizadores
  dscStatuscomercial?: string;
  dscStatustecnico?: string;
  flgPostoBloqueado?: FlagBit;
  temAcordo?: SimNao;
  indKloQrcode?: SimNao;
  dscCodold?: string;

  // Operacional
  numBomba?: number;
  canal?: number;
  countRecs?: number;

  // Posto mais próximo / mais barato
  distancia?: number | null;
  vlrPrecoMaisPertoMaisBarato?: number | null;
  codPostoMaisPertoMaisBarato?: number | null;
  dscFantasiaPostoMaisPerto?: string;

  // Monitor / variações
  diferencaDtInicioValidade?: number;
  monitor24h?: Monitor24h;
  monitor30d: Monitor30d;
  difPreco?: number;
}

export interface FrotaResponseBody {
  retorno: FrotaRetornoItem[];
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private static readonly MAX_ALERTAS_DETALHE = 15;
  private localStorageService = inject(LocalStorageService);
  private readonly USER_GUID = this.localStorageService.getItem('guid');

  constructor(
    private readonly http: HttpClient,
    private readonly snackBar: SnackBarService,
  ) {}

  // ============================================================
  // API pública - chamadas HTTP
  // ============================================================

  getEconomiaPotencialPrecoBomba(): Observable<number> {
    return this.http
      .get<PrecoBombaResponse>(
        `${environment.host_ctf_service}${CONFIG_URLS_API.API_DashboardFrota_EconomiaPotencialPrecoBomba}`,
        { headers: this.buildHeaders() },
      )
      .pipe(
        tap((response: PrecoBombaResponse) => {
          if (response && !response.sucesso) {
            this.snackBar.flashMessage(
              'Não foi possível obter a economia potencial.',
              ['snackbar-error'],
              'Fechar',
              0,
            );
          }
        }),
        map((body: PrecoBombaResponse) =>
          body.sucesso ? body.retorno.economiaPotencial : 0,
        ),
        catchError(() => of(0)),
      );
  }

  getPrecoBomba(_combustivel: number): Observable<FrotaRetornoItem[]> {
    return this.http
      .post<PrecoBombaFrotaResponse>(
        `${environment.host_ctf_service}${CONFIG_URLS_API.API_DashboardFrota_PrecoBomba}`,
        null,
        { headers: this.buildHeaders() },
      )
      .pipe(
        tap((response: PrecoBombaFrotaResponse) => {
          if (response && !response.sucesso) {
            this.snackBar.flashMessage(
              'Não foi possível obter os alertas de preço de bomba.',
              ['snackbar-error'],
              'Fechar',
              0,
            );
          }
        }),
        map((body: PrecoBombaFrotaResponse) =>
          body.sucesso ? body.retorno : [],
        ),
        catchError(() => of([])),
      );
  }

  // ============================================================
  // API pública - regras de domínio
  // ============================================================

  /**
   * Converte a lista bruta vinda da API (`FrotaRetornoItem[]`) em alertas
   * prontos para exibição (`PrecoAlertaDestaque[]`).
   *
   * Critério de inclusão: combustível S10 **e** `monitor30d` indicando
   * movimento (`'30D-SUBIU'` ou `'30D-DESCEU'`). Todos os alertas gerados
   * têm janela `'30d'`.
   *
   * Ordenação: `datUltimoAbastecimento` desc; itens sem essa data vão pro
   * fim. Empates desempatam por `percentual` desc (`null` tratado como `-1`).
   * Aplica o teto `MAX_ALERTAS_DETALHE`.
   */
  mapItensToAlertas(items: FrotaRetornoItem[]): PrecoAlertaDestaque[] {
    return items
      .filter((item) => this.ehDieselS10(item))
      .filter((item) => this.direcaoMonitor30d(item.monitor30d) !== null)
      .map((item) => this.criarAlertaDeItem(item))
      .filter((alerta): alerta is PrecoAlertaDestaque => alerta !== null)
      .sort((a, b) => this.compararAlertasPorData(a, b));
    // .slice(0, DashboardService.MAX_ALERTAS_DETALHE);
  }

  /**
   * Conta itens com tendência de variação nas últimas 24h
   * (`monitor24h` igual a `'24H-SUBIU'` ou `'24H-DESCEU'`).
   */
  contarAlertas24h(items: FrotaRetornoItem[]): number {
    return items.filter(
      (i) => i.monitor24h === '24H-SUBIU' || i.monitor24h === '24H-DESCEU',
    ).length;
  }

  // ============================================================
  // Helpers privados - regras de domínio
  // ============================================================

  private ehDieselS10(item: FrotaRetornoItem): boolean {
    return (item.dscComb ?? '').toUpperCase().includes('S10');
  }

  // ============================================================
  // Helpers privados - construção de alertas
  // ============================================================

  private criarAlertaDeItem(
    item: FrotaRetornoItem,
  ): PrecoAlertaDestaque | null {
    const direcao = this.direcaoMonitor30d(item.monitor30d);
    const atual = this.num(item.numPrecoNaBombaAtual);
    if (direcao === null || atual === null) {
      return null;
    }

    const anterior = this.num(item.numPrecoAnterior);
    const variacao = this.calcularVariacao24h(anterior, atual);
    const refDate = item.datUltimoAbastecimento ?? undefined;
    const combSlug = (item.dscComb ?? 'S10').replace(/\s+/g, '-');
    const pctTag = variacao !== null ? variacao.percentual.toFixed(2) : 'na';

    return {
      chave: `${item.codPosto}-${combSlug}-30d-${direcao}-${pctTag}`,
      direcao,
      janela: '30d',
      percentual: variacao?.percentual ?? null,
      tituloLocal: this.buildTituloLocal(item),
      precoAnterior: anterior !== null ? anterior : undefined,
      precoAtual: atual,
      momentoRelativo: this.formatMomentoRelativo(refDate),
      momentoIso: refDate,
      melhorAlternativa: this.criarMelhorAlternativa(item) ?? undefined,
    };
  }

  /**
   * Constrói o objeto da "melhor alternativa" (posto mais perto e mais
   * barato) a partir do item bruto. Retorna `null` quando qualquer um
   * dos campos de origem está ausente/inválido — assim o card pode
   * decidir entre renderizar o painel ou o placeholder de vazio.
   */
  private criarMelhorAlternativa(
    item: FrotaRetornoItem,
  ): PrecoAlertaMelhorAlternativa | null {
    const distanciaKm = this.num(item.distancia ?? null);
    const preco = this.num(item.vlrPrecoMaisPertoMaisBarato ?? null);
    const codPosto = item.codPostoMaisPertoMaisBarato ?? null;
    const fantasia = (item.dscFantasiaPostoMaisPerto ?? '').trim();
    if (
      distanciaKm === null ||
      preco === null ||
      codPosto === null ||
      fantasia.length === 0
    ) {
      return null;
    }
    return { codPosto, fantasia, distanciaKm, preco };
  }

  /**
   * Calcula a variação imediata anterior→atual. Retorna `null` quando os
   * preços não permitem inferir movimento (faltam dados, são iguais ou o
   * anterior é inválido).
   */
  private calcularVariacao24h(
    anterior: number | null,
    atual: number | null,
  ): { direcao: PrecoAlertaDirecao; percentual: number } | null {
    if (
      anterior === null ||
      atual === null ||
      anterior <= 0 ||
      atual === anterior
    ) {
      return null;
    }
    return {
      direcao: atual > anterior ? 'subiu' : 'desceu',
      percentual: Math.abs((atual - anterior) / anterior) * 100,
    };
  }

  /**
   * Mapeia `monitor30d` para a direção do alerta. Retorna `null` quando a
   * string não indica movimento.
   */
  private direcaoMonitor30d(
    monitor30d: Monitor30d | undefined,
  ): PrecoAlertaDirecao | null {
    if (monitor30d === '30D-SUBIU') {
      return 'subiu';
    }
    if (monitor30d === '30D-DESCEU') {
      return 'desceu';
    }
    return null;
  }

  /**
   * Compara dois alertas por `momentoIso` desc (mais recente primeiro).
   * Itens sem data válida vão pro fim. Empates desempatam por `percentual`
   * desc (`null` tratado como `-1`).
   */
  private compararAlertasPorData(
    a: PrecoAlertaDestaque,
    b: PrecoAlertaDestaque,
  ): number {
    const ta = a.momentoIso ? Date.parse(a.momentoIso) : NaN;
    const tb = b.momentoIso ? Date.parse(b.momentoIso) : NaN;
    const va = Number.isNaN(ta) ? -Infinity : ta;
    const vb = Number.isNaN(tb) ? -Infinity : tb;
    if (vb !== va) {
      return vb - va;
    }
    return (b.percentual ?? -1) - (a.percentual ?? -1);
  }

  private buildTituloLocal(item: FrotaRetornoItem): string {
    const codigo = String(item.codPosto).padStart(5, '0');
    const fantasia = (item.dscFantasia ?? '').trim() || 'Posto';
    const endereco = (item.dscEndereco ?? '').trim();
    return endereco
      ? `${codigo} - ${fantasia} (${endereco})`
      : `${codigo} - ${fantasia}`;
  }

  // ============================================================
  // Helpers privados - utilitários
  // ============================================================

  private buildHeaders(): { [k: string]: string } {
    return {
      'Content-Type': 'application/json',
      userguid: this.USER_GUID,
    };
  }

  private formatMomentoRelativo(iso: string | null | undefined): string {
    if (!iso) {
      return 'Há pouco';
    }
    const t = Date.parse(iso);
    if (Number.isNaN(t)) {
      return 'Há pouco';
    }
    let diffMs = Date.now() - t;
    if (diffMs < 0) {
      diffMs = 0;
    }
    const min = Math.floor(diffMs / 60000);
    if (min < 1) {
      return 'Agora há pouco';
    }
    if (min < 60) {
      return `Há ${min} min`;
    }
    const h = Math.floor(min / 60);
    if (h < 24) {
      return `Há ${h} h`;
    }
    const d = Math.floor(h / 24);
    return `Há ${d} d`;
  }

  private num(value: number | null | undefined): number | null {
    if (value == null || typeof value !== 'number' || !Number.isFinite(value)) {
      return null;
    }
    return value;
  }
}
